import { useState } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { UserRole } from 'domain/user';
import { useIsSwUser } from 'hooks/useIsSwUser';

import { Dropdown } from '../../../../components/Dropdown';
import { Tooltip } from '../../../../components/Tooltip';
import { useUserQuery } from '../../../../features/user/useUserQuery';
import { translate } from '../../../../services/i18n';
import { StyledCrmExportButton, StyledCrmExportBulkButton } from '../CrmExportContainer/styles';

import { StyledCrmExportMenu, StyledCrmExportMenuItem, StyledCrmExportMenuLink } from './styles';

export const NotConnectedCrmButton = ({
  variant = 'single',
  handleEnableExportTrack,
  isDisabled = false,
}: {
  variant?: 'single' | 'bulk';
  handleEnableExportTrack: (action: string, userRole: UserRole) => void;
  isDisabled?: boolean;
}) => {
  const { data: userData } = useUserQuery();
  const hasAccessToManageCrm = userData?.hasAccessToManageCrm ?? false;
  const userRole = userData?.userRole ?? UserRole.USER;

  const isSwUser = useIsSwUser();
  const accountAdminEmails = userData?.accountAdminEmails ?? [];

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const handleExportClick = () => {
    handleEnableExportTrack('not integrated', userRole);
    setIsDropdownOpened(true);
  };

  let tooltipContent = translate('contact_card.connect_crm.disabled.tooltip');
  if (!isSwUser && accountAdminEmails.length > 0) {
    tooltipContent += ` - ${accountAdminEmails.join(', ')}`;
  }

  return (
    <>
      {variant === 'single' ? (
        <StyledCrmExportButton
          onClick={handleExportClick}
          isActive={isDropdownOpened}
          data-automation="NotConnectedCrmButton"
          disabled={isDisabled}
        >
          {translate('contact_card.export_crm')}
        </StyledCrmExportButton>
      ) : (
        <StyledCrmExportBulkButton
          onClick={handleExportClick}
          variant="flat"
          isActive={isDropdownOpened}
          data-automation="NotConnectedCrmButtonBulk"
          disabled={isDisabled}
          iconName={isDropdownOpened ? 'chev-up' : 'chev-down'}
          iconSize="xs"
          placement="right"
        >
          {translate('contact_card.export_crm')}
        </StyledCrmExportBulkButton>
      )}
      <Dropdown isOpened={isDropdownOpened} onClose={() => setIsDropdownOpened(false)} placement="right" minWidth={208}>
        {hasAccessToManageCrm ? (
          <StyledCrmExportMenu data-automation="NotConnectedCrmMenu">
            <StyledCrmExportMenuItem>
              <SWReactIcons iconName="salesforce" size="xs" iconColor={colorsPalettes.carbon[200]} />
              <StyledCrmExportMenuLink target="_blank" href="https://pro.similarweb.com/#/sales/management">
                {translate('contact_card.connect_sf')}
              </StyledCrmExportMenuLink>
            </StyledCrmExportMenuItem>
            <StyledCrmExportMenuItem data-automation="NotConnectedCrmMenuItem">
              <SWReactIcons iconName="hubspot" size="xs" iconColor={colorsPalettes.carbon[200]} />
              <StyledCrmExportMenuLink target="_blank" href="https://pro.similarweb.com/#/sales/management">
                {translate('contact_card.connect_hs')}
              </StyledCrmExportMenuLink>
            </StyledCrmExportMenuItem>
          </StyledCrmExportMenu>
        ) : (
          <Tooltip content={tooltipContent} placement="top" minWidth={230}>
            <StyledCrmExportMenu>
              <StyledCrmExportMenuItem isDisabled>
                <SWReactIcons iconName="salesforce" size="xs" iconColor={colorsPalettes.carbon[200]} />
                {translate('contact_card.connect_sf')}
              </StyledCrmExportMenuItem>
              <StyledCrmExportMenuItem isDisabled>
                <SWReactIcons iconName="hubspot" size="xs" iconColor={colorsPalettes.carbon[200]} />
                {translate('contact_card.connect_hs')}
              </StyledCrmExportMenuItem>
            </StyledCrmExportMenu>
          </Tooltip>
        )}
      </Dropdown>
    </>
  );
};
